import apiService from './api';
import { ApiResponse, RespostaPaginada } from '../types';

// Tamanhos de pagina padrao usados quando o chamador nao informa `per_page`.
// O de atividades preserva o comportamento anterior ao endpoint paginado.
const ALERTAS_POR_PAGINA_PADRAO = 15;
const ATIVIDADES_POR_PAGINA_PADRAO = 8;

/**
 * Bloco de paginacao devolvido pelos endpoints de lista da API.
 *
 * Modelado aqui, e nao reaproveitando `PaginatedResponse` de `types/index.ts`,
 * porque aquele tipo achata os campos na raiz da resposta enquanto a API real
 * devolve `pagination` aninhado.
 */

export interface DashboardData {
  veiculos: {
    total: number;
    ativos: number;
    inativos: number;
    manutencao: number;
  };
  motoristas: {
    total: number;
    ativos: number;
    inativos: number;
    cnh_vencendo: number;
  };
  viagens: {
    total: number;
    concluidas: number;
    em_andamento: number;
    km_total: number;
  };
  custos: {
    combustivel: number;
    manutencao: number;
    despesas: number;
    total: number;
  };
  abastecimentos: {
    total: number;
    litros_total: number;
    valor_medio: number;
  };
  manutencoes: {
    total: number;
    preventivas: number;
    corretivas: number;
  };
  despesas: {
    total: number;
    pagas: number;
    pendentes: number;
    /** Gasto por categoria no período, da maior para a menor. */
    por_categoria: DespesaPorCategoria[];
  };
  /**
   * Data do lançamento mais recente da empresa, sem filtro de período.
   *
   * Serve para explicar um dashboard vazio: sem ela, a tela não distingue
   * "não há registro nenhum" de "não há registro NESTE período".
   */
  ultimo_lancamento: string | null;
}

export interface DespesaPorCategoria {
  categoria: string;
  /** Rótulo já pronto para exibir — vem do servidor, onde a lista de categorias mora. */
  rotulo: string;
  valor: number;
  quantidade: number;
}

export interface ConsumoCombustivelData {
  abastecimentos: any[];
  estatisticas_por_veiculo: any[];
  estatisticas_por_tipo: any[];
  resumo: {
    total_abastecimentos: number;
    total_litros: number;
    total_valor: number;
    valor_medio_litro: number;
    consumo_medio: number;
  };
}

export interface ManutencoesData {
  manutencoes: any[];
  estatisticas_por_veiculo: any[];
  estatisticas_por_tipo: any[];
  resumo: {
    total_manutencoes: number;
    total_valor: number;
    valor_medio: number;
    preventivas: number;
    corretivas: number;
  };
}

export interface DespesasData {
  despesas: any[];
  estatisticas_por_categoria: any[];
  estatisticas_por_status: any[];
  estatisticas_por_veiculo: any[];
  resumo: {
    total_despesas: number;
    total_valor: number;
    valor_medio: number;
    pagas: number;
    pendentes: number;
  };
}

export interface ViagensData {
  viagens: any[];
  estatisticas_por_veiculo: any[];
  estatisticas_por_motorista: any[];
  estatisticas_por_tipo: any[];
  resumo: {
    total_viagens: number;
    total_km: number;
    total_custo: number;
    km_medio: number;
    concluidas: number;
    em_andamento: number;
  };
}

/** Contagem de uma lista de alerta: quantos existem e quantos vieram no painel. */
export interface ResumoDaLista {
  rotulo: string;
  total: number;
  exibidos: number;
  truncado: boolean;
}

/** Identificador de cada lista de alerta, usado no caminho da rota paginada. */
export type SlugAlerta =
  | 'licenciamento-vencendo'
  | 'licenciamento-vencido'
  | 'seguro-vencendo'
  | 'seguro-vencido'
  | 'cnh-vencendo'
  | 'cnh-vencida'
  | 'manutencoes-preventivas'
  | 'despesas-vencendo'
  | 'despesas-vencidas';

export interface ResumoAlertas {
  limite_por_lista: number;
  total_geral: number;
  listas: Record<SlugAlerta, ResumoDaLista>;
}

export interface AlertasData {
  veiculos: {
    licenciamento_vencendo: any[];
    licenciamento_vencido: any[];
    seguro_vencendo: any[];
    seguro_vencido: any[];
  };
  motoristas: {
    cnh_vencendo: any[];
    cnh_vencida: any[];
  };
  manutencoes: {
    preventivas_pendentes: any[];
  };
  despesas: {
    vencendo: any[];
    vencidas: any[];
  };
  // Opcional de proposito: um backend anterior a este campo continua valido em
  // runtime, e cabe a camada de apresentacao decidir o fallback quando faltar.
  resumo?: ResumoAlertas;
}

export type AtividadeTipo = 'abastecimento' | 'manutencao' | 'despesa' | 'viagem';

export interface AtividadeRecente {
  tipo: AtividadeTipo;
  id: number;
  titulo: string;
  descricao: string;
  data: string | null;
}

export const relatoriosService = {
  // Dashboard principal
  getDashboard: async (periodo: string = 'mes'): Promise<ApiResponse<DashboardData>> => {
    const response = await apiService.get<ApiResponse<DashboardData>>(`/v1/relatorios/dashboard?periodo=${periodo}`);
    return response;
  },

  // Relatório de consumo de combustível
  getConsumoCombustivel: async (filters: {
    data_inicio?: string;
    data_fim?: string;
    veiculo_id?: number;
    tipo_combustivel?: string;
  } = {}): Promise<ApiResponse<ConsumoCombustivelData>> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiService.get<ApiResponse<ConsumoCombustivelData>>(`/v1/relatorios/consumo-combustivel?${params.toString()}`);
    return response;
  },

  // Relatório de manutenções
  getManutencoes: async (filters: {
    data_inicio?: string;
    data_fim?: string;
    veiculo_id?: number;
    tipo?: string;
  } = {}): Promise<ApiResponse<ManutencoesData>> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiService.get<ApiResponse<ManutencoesData>>(`/v1/relatorios/manutencoes?${params.toString()}`);
    return response;
  },

  // Relatório de despesas
  getDespesas: async (filters: {
    data_inicio?: string;
    data_fim?: string;
    veiculo_id?: number;
    categoria?: string;
    status?: string;
  } = {}): Promise<ApiResponse<DespesasData>> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiService.get<ApiResponse<DespesasData>>(`/v1/relatorios/despesas?${params.toString()}`);
    return response;
  },

  // Relatório de viagens
  getViagens: async (filters: {
    data_inicio?: string;
    data_fim?: string;
    veiculo_id?: number;
    motorista_id?: number;
    tipo?: string;
  } = {}): Promise<ApiResponse<ViagensData>> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiService.get<ApiResponse<ViagensData>>(`/v1/relatorios/viagens?${params.toString()}`);
    return response;
  },

  // Relatório de eficiência por veículo
  getEficienciaVeiculos: async (filters: {
    data_inicio?: string;
    data_fim?: string;
  } = {}): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiService.get<ApiResponse<any[]>>(`/v1/relatorios/eficiencia-veiculos?${params.toString()}`);
    return response;
  },

  // Relatório de alertas
  getAlertas: async (): Promise<ApiResponse<AlertasData>> => {
    const response = await apiService.get<ApiResponse<AlertasData>>('/v1/relatorios/alertas');
    return response;
  },

  // Pagina de uma lista de alerta especifica. O item muda conforme a lista
  // (veiculo, motorista, manutencao ou despesa), por isso `any`, como nos
  // arrays de `AlertasData`.
  getAlertasLista: async (
    lista: SlugAlerta,
    page: number = 1,
    perPage: number = ALERTAS_POR_PAGINA_PADRAO
  ): Promise<RespostaPaginada<any>> => {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    const response = await apiService.get<RespostaPaginada<any>>(
      `/v1/relatorios/alertas/${encodeURIComponent(lista)}?${params.toString()}`
    );
    return response;
  },

  // Feed de atividades individuais recentes.
  // Recebe opcoes nomeadas em vez de argumentos posicionais porque o endpoint
  // passou a aceitar `page` alem do tamanho de pagina (o antigo `limite` esta
  // depreciado no servidor).
  getAtividadesRecentes: async (
    opcoes: { page?: number; per_page?: number } = {}
  ): Promise<RespostaPaginada<AtividadeRecente>> => {
    const { page = 1, per_page = ATIVIDADES_POR_PAGINA_PADRAO } = opcoes;
    const params = new URLSearchParams({ page: String(page), per_page: String(per_page) });
    const response = await apiService.get<RespostaPaginada<AtividadeRecente>>(
      `/v1/relatorios/atividades-recentes?${params.toString()}`
    );
    return response;
  }
};
